<template>
    <div v-if="device">

        <loading-state
                fullscreen
                v-if="loading">
        </loading-state>

<!--        <job-complete-nav></job-complete-nav>-->

        <div class="container repair-container job-complete">

            <h2 class="text-center" v-if="!device.review_complete">Review the repair and confirm the status</h2>

            <div id="complete" class="card card-body pt-3 pb-4" v-if="device.review_complete">

                <!-- If the job type is not a re-repair -->
                <template v-if="job.job_type !== 're-repair'">
                    <!-- Review Completed -->
                    <empty-state
                            v-if="!device.complete"
                            icon="checklist"
                            title="Review completed"
                            :subtitle="`Repair status: ${deviceStatus.title}`">
                    </empty-state>

                    <!-- Repair Completed -->
                    <empty-state
                            v-if="device.complete"
                            statusIcon="success"
                            title="This repair has been completed">
                    </empty-state>
                </template>

                <!-- If the job type is a re-repair -->
                <template v-if="job.job_type === 're-repair'">
                    <!-- Re-repair Completed -->
                    <empty-state
                            v-if="device.complete"
                            icon="repair-kits-packing"
                            title="This repair has been completed"
                            subtitle="Please add the device to the currently open bulk shipment">
                    </empty-state>

                    <!-- Device Beyond Economic Repair -->
                    <empty-state
                            v-if="deviceStatus.title === 'Device Beyond Repair'"
                            icon="ber"
                            title="Device Beyond Economic Repair"
                            subtitle="Please add the device to the currently open BER shipment">
                    </empty-state>
                </template>

                <div class="row">
                    <div class="col-md-6 offset-md-3" v-if="job.job_type === 'mail-in' && deviceStatus.title === 'Completed'">
                        <button class="btn btn-block btn-info btn-lg mb-4 px-5" @click="printTheDpdLabel" :disabled="printing"><i v-if="printing" class="fa fa-spinner fa-pulse"></i> Print DPD label</button>
                    </div>
                    <div class="col-md-5 offset-md-1">
                        <button class="btn btn-block btn-outline-info mb-2 px-5" v-b-toggle.progress-step-list @click="scrollToProgressSteps()">Make changes</button>
                    </div>
<!--                    <div class="col-md-5 offset-md-1">-->
<!--                        <button class="btn btn-block btn-info mb-2 px-5" v-b-toggle.progress-step-list @click="scrollToProgressSteps()">Make changes</button>-->
<!--                    </div>-->
                    <div class="col-md-5 m-auto">
                        <router-link :to="{ name: 'job'}" class="btn btn-block btn-success mb-2 px-5">Back to job</router-link>
                    </div>
                </div>
            </div>

            <!-- Job Details -->
            <div id="job-details" class="card card-body pb-0">
                <h5>Job details</h5>
                <dl>
                    <dt>Order ID</dt>
                    <dd>{{ jobId }}</dd>
                </dl>
                <dl v-if="deviceStatus">
                    <dt>Status</dt>
                    <dd>{{ deviceStatus.title }}</dd>
                </dl>
<!--                <dl class="mb-0">-->
<!--                    <dt>Repair duration</dt>-->
<!--                    <dd>Repair time: 10:32 - 11:21</dd>-->
<!--                    <dd>Duration: 49m</dd>-->
<!--                </dl>-->
            </div>

            <!-- Device Details -->
            <div id="device-details" class="card card-body pb-0">
                <h5>Device</h5>
                <div class="media mb-4">
                    <div class="media-left">
                        <img v-if="device.version_image" :src="device.version_image" class="img-2x">
                        <template v-if="!device.version_image">
                            <icon-mobile v-if="device.type === 'phone'" size="2x" class="icon-all-muted"></icon-mobile>
                            <icon-tablet v-if="device.type === 'tablet'" size="2x" class="icon-all-muted"></icon-tablet>
                        </template>
                    </div>
                    <div class="media-body">
                        <h5 class="mb-2">{{ device.title_with_colour }}</h5>
                        <p class="mb-3 text-muted text-sm">IMEI: {{ device.imei }}</p>
                        <ul class="list-unstyled text-muted text-sm">
                            <li v-for="repair in deviceRepairs" :key="repair.id">
                                {{repair.title}}
                            </li>
                        </ul>
                    </div>
                </div>
                <template v-if="deviceNotes.length > 0">
                    <button class="btn btn-outline-light mb-4" v-b-toggle.device-notes-collapse>Device Notes ({{ deviceNotes.length }})</button>
                    <b-collapse id="device-notes-collapse">
                        <note-item :jobNotes="deviceNotes" small></note-item>
                    </b-collapse>
                </template>
            </div>

            <!-- Repair Images -->
            <div id="repair-images" class="card card-body pb-0">
                <h5>Repair photos</h5>
                <div class="card-grid grid-col-2 grid-col-sm-3 grid-col-md-6">
                    <image-item
                            v-for="(imagePath, key) in imagePaths" :key="key"
                            :imageType="imagePath.type"
                            :imageData="imagePath.path"
                            small>
                    </image-item>
                </div>
                <div class="row">
                    <div class="col-6">
                        <router-link :to="{name: 'repair.beforePictures'}" class="btn btn-block btn-info btn-sm mb-4 px-1">Add before pictures</router-link>
                    </div>
                    <div class="col-6">
                        <router-link :to="{name: 'repair.afterPictures'}" class="btn btn-block btn-info btn-sm mb-4 px-1">Add after pictures</router-link>
                    </div>
                </div>
            </div>

            <!-- IRIS & Symptom Codes -->
            <div id="iris-symptom-codes" class="card card-body pb-0" v-if="device.make_id === '2'">
                <h5>Symptom &amp; IRIS codes</h5>
                <dl>
                    <dt>Symptom codes</dt>
                    <dd v-if="symptomCodeOne"><span class="font-weight-normal">Code One:</span> {{ symptomCodeOne.title }}</dd>
                    <dd v-if="symptomCodeTwo"><span class="font-weight-normal">Code Two:</span> {{ symptomCodeTwo.title }}</dd>
                    <dd v-if="symptomCodeThree"><span class="font-weight-normal">Code Three:</span> {{ symptomCodeThree.title }}</dd>
                </dl>
                <router-link :to="{name: 'repair.symptomCodes'}" class="btn btn-info btn-sm mb-4 align-self-start">Edit symptom codes</router-link>
                <dl>
                    <dt>IRIS codes</dt>
                    <dd v-if="irisCodeOne"><span class="font-weight-normal">Code One:</span> {{ irisCodeOne.description }}</dd>
                    <dd v-if="irisCodeTwo"><span class="font-weight-normal">Code Two:</span> {{ irisCodeTwo.description }}</dd>
                    <dd v-if="irisCodeThree"><span class="font-weight-normal">Code Three:</span> {{ irisCodeThree.description }}</dd>
                    <dd v-if="irisCodeFour"><span class="font-weight-normal">Code Four:</span> {{ irisCodeFour.description }}</dd>
                </dl>
                <router-link :to="{name: 'repair.irisCodes'}" class="btn btn-info btn-sm mb-4 align-self-start">Edit IRIS codes</router-link>
            </div>

            <!-- Confirmed Problems -->
            <div id="confirmed-problems" class="card card-body pb-0" v-if="confirmedProblems.length > 0">
                <h5>Confirmed problems</h5>
                <dl>
                    <dt>Problems</dt>
                    <dd v-for="problem in confirmedProblems" :key="problem.id">
                        {{ problem.title }}
                    </dd>
                </dl>
                <router-link :to="{name: 'repair.confirmProblems'}" class="btn btn-info btn-sm mb-4 align-self-start">Edit problems</router-link>
            </div>

            <!-- Parts Used -->
            <div id="parts-used" class="card card-body pb-0">
                <h5>Parts used</h5>
                <empty-state
                        align="left"
                        faIcon="microchip"
                        inline
                        small
                        title="No parts were used"
                        v-if="deviceParts.length === 0 && devicePartsFaulty.length === 0">
                </empty-state>
                <dl v-if="deviceParts.length > 0">
                    <dt>Parts used</dt>
                    <dd v-for="part in deviceParts" :key="part.id">
                        {{ part.stock_item_title }}
                    </dd>
                </dl>
                <dl v-if="devicePartsFaulty.length > 0">
                    <dt>Faulty parts</dt>
                    <dd v-for="part in devicePartsFaulty" :key="part.id">
                        {{ part.stock_item_title }}
                    </dd>
                </dl>
                <router-link :to="{name: 'repair.addParts'}" class="btn btn-info btn-sm mb-4 align-self-start">Edit parts</router-link>
            </div>

            <!-- Test Checklist -->
            <div id="test-checklist" class="card card-body d-none">
                <h5>Test checklist</h5>
                <dl class="mb-0">
                    <dt>Tests performed</dt>
                    <ul class="fa-ul ml-4 font-weight-bold">
                        <li><i class="fa fa-check fa-li text-success"></i> Front camera works - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Ear speaker - Use voice memo app - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Drag application across the whole screen - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Home button clicks correctly - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Proximity sensor - Use Voice memo app and record text message. Play back message and place finger over proximity sensor, screen should go blank. - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Rear camera works - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Check for dust underneath the screen. Also look for marks using the white background on Safari - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Dial in *#0*# run through checks before sticking battery down  - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Dial in *#0*# run sensor test, barometer number has to raise when running the water test - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Run MLC (water resistance test) on *#0*# once complete reset device - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Home button calibration - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Battery reset - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Water resistance check on Galaxy MUST - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Galaxy Diagnostics - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> Dial in *#0*# sensor section, check finger print scanner before sealing  - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> If battery changed add old & new serial number to GSPN - Passed</li>
                        <li><i class="fa fa-check fa-li text-success"></i> If OCTA changed add old & new serial number to GSPN - Passed</li>
                    </ul>
                </dl>
            </div>

            <!-- Samsung Notes -->
            <div id="samsung-notes" class="card card-body d-none">
                <h5>Notes to Samsung</h5>
                <dl class="mb-0">
                    <dt>Note</dt>
                    <dd>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</dd>
                </dl>
            </div>

            <!-- Status select -->
            <div id="status-select" class="card card-body" v-if="!device.review_complete">
                <template v-if="!loadingRevisit">
                    <div class="form-group">
                        <h5>Select the status</h5>
                        <select class="custom-select" v-model="selectedStatus">
                          <optgroup v-if="job.job_type === 'refurbishment'" label="Completed">
                            <option value="37">Repaired Pending QC</option>
                          </optgroup>
                          <optgroup v-else label="Completed">
                            <option value="1">Completed</option>
                          </optgroup>
                            <optgroup label="Other statuses">
                                <option
                                        v-for="status in filteredDeviceStatuses"
                                        :key="status.key"
                                        :value="status.id">
                                    {{ status.title }}
                                </option>
                            </optgroup>
                        </select>
                    </div>

                    <template v-if="soErrors.length !== 0">
                        <h5>There have been some errors on this service order</h5>
                        <p v-for="(error, index) in soErrors" :key="index">{{error}}</p>
                    </template>

                    <!-- Revisit not required -->
<!--                    <template v-if="!revisitRequired">-->
                        <button class="btn btn-success btn-lg mx-auto px-5" v-if="serviceOrderRequired && !revisitRequired" @click="completeServiceOrder">Complete Service Order<template v-if="device.so_number">: {{ device.so_number }}</template></button>

                        <button class="btn btn-lg btn-success mx-auto px-5" v-if="!serviceOrderRequired" @click="checkInWarrantyRequired()" :disabled="!selectedStatus ||  serviceOrderRequired">
                            <template v-if="!selectedStatus">Select a status</template>
                            <template v-if="selectedStatus">Confirm status and finish repair</template>
                        </button>
<!--                    </template>-->

                    <!-- Revisit bits -->
                    <template v-if="!isRefurbOrMailIn">
                        <template v-if="revisitRequired && isOnline && !isSelfServe">
                            <h2 class="mt-4">Revisit Required</h2>
                            <p>A revisit booking is required for this status change, either click below to book a revisit or change the status.</p>
                            <button type="button" class="btn btn-danger btn-lg mx-auto px-5" @click="bookRevisitLoadJob()">Book Revisit</button>
                        </template>

                        <template v-if="revisitRequired && isOffline">
                            <h2 class="mt-4">Revisit Required</h2>
                            <p>A revisit is required for this order, however you are currently offline.</p>
                        </template>

                        <template v-if="revisitRequired && isSelfServe">
                            <h2 class="mt-4">Revisit Required</h2>
                            <p>A revisit is required to complete this job, as the customer is not available click the button below to send them a self serve revisit booking form.</p>
                            <button type="button" class="btn btn-danger btn-lg mx-auto px-5" @click="bookRevisitSelfServe()">Send Form</button>
                        </template>
                    </template>
                    <template v-if="isRefurbOrMailIn && revisitRequired">
                        <div class="alert alert-info mb-0 mt-4">
                            <animated-info></animated-info>
                            <div class="alert-content">Please return this device to the office.</div>
                        </div>
                    </template>
                </template>

                <!-- Loading revisit -->
                <empty-state
                        v-if="loadingRevisit"
                        class="bg-transparent"
                        statusIcon="loading"
                        title="Loading revisit booking"
                        subtitle="Please wait">
                </empty-state>

            </div>

            <!-- Footer -->
<!--            <job-complete-footer class="active"></job-complete-footer>-->

        </div>
    </div>
</template>

<script>
    import {mapGetters, mapActions} from 'vuex';

    import {firebase} from '@firebase/app';
    import '@firebase/storage';
    import { v4 as uuidv4 } from 'uuid';

    export default {

        props:['jobId', 'deviceId'],

        data() {
            return {
                imagePaths:[],
                isSelfServe: false,
                loadingRevisit: false,
                loading: false,
                revisitRequired: false,
                selectedStatus: null,
                serviceOrderRequired: false,
                showIrisSymptomCodes: false,
                soErrors: [],
                printing: false
            }
        },

        computed: {
            ...mapGetters([
                "confirmedProblems",
                "device",
                "deviceAssets",
                "deviceNotes",
                "devicePartsFaulty",
                "deviceParts",
                "deviceRepairs",
                "deviceStatus",
                "deviceStatuses",
                "irisCodeOne",
                "irisCodeTwo",
                "irisCodeThree",
                "irisCodeFour",
                "job",
                "symptomCodeOne",
                "symptomCodeTwo",
                "symptomCodeThree",
                "isRefurbOrMailIn"
            ]),

            filteredDeviceStatuses() {
                let filtered = this.deviceStatuses.filter((el) => {
                    return (el.title && el.title !== "Completed" && el.title !== "Repaired Pending QC")
                });

                return filtered;
            }
        },

        watch: {
            job(newJob) {
                if (newJob.so_complete && !newJob.so_error) {
                    this.updateDeviceStatus();
                }

                if (newJob.so_complete && newJob.so_error) {
                    this.soErrors = JSON.parse(newJob.so_errors);

                    if (this.soErrors[0] && this.soErrors[0] === "This service order is already closed.") {
                        this.updateDeviceStatus();
                    } else {
                        this.loading = false;
                    }
                }
            },

            jobId(jobId) {
                if (jobId) {
                    this.loadJob({
                        jobId: this.jobId
                    });
                }
            }
        },

        mounted() {
            this.loading = false;
            this.loadDeviceForJob({
                jobId: this.jobId,
                deviceId: this.deviceId,
            });
            this.loadDeviceAssets({
                jobId: this.jobId,
                deviceId:this.deviceId
            });
            this.listImages();
            this.loadAddedConfirmedProblems({
                jobId: this.jobId,
                deviceId: this.deviceId
            });
            this.loadDeviceNotes({
                jobId: this.jobId,
                deviceId: this.deviceId
            });
            this.loadDeviceRepairs({
                jobId: this.jobId,
                deviceId: this.deviceId
            });
            this.loadDeviceStatuses();
            this.loadFaultyParts({
                jobId:this.jobId,
                deviceId: this.deviceId
            });
            this.loadIrisCodesForDevice({
                jobId:this.jobId,
                deviceId:this.deviceId
            });
            this.loadParts({
                jobId:this.jobId,
                deviceId: this.deviceId
            });
            this.loadSymptomCodesForDevice({
                jobId:this.jobId,
                deviceId:this.deviceId
            });
            this.setCurrentStep({
                jobId: this.jobId,
                deviceId: this.deviceId,
                currentStep: this.$route.name,
            });
        },

        methods: {
            ...mapActions([
                "displayToast",
                "loadAddedConfirmedProblems",
                "loadDeviceAssets",
                "loadDeviceForJob",
                "loadDeviceNotes",
                "loadFaultyParts",
                "loadJob",
                "loadParts",
                "loadDeviceRepairs",
                "loadDeviceStatus",
                "loadDeviceStatuses",
                "loadIrisCodesForDevice",
                "loadSymptomCodesForDevice",
                "printDpdLabel",
                "setCurrentStep",
                "setDeviceComplete",
                "setDeviceRepairedPendingQC",
                "setDeviceNotComplete",
                "setDeviceReviewComplete",
                "setDeviceStatus"
            ]),

            /** Revisit load job */
            bookRevisitLoadJob() {
                this.loadingRevisit = true;
                this.$http.get(`/api/v4/warranty/booking/build/${this.job.actual_order_id}`)
                    .then(res => {
                        localStorage.revisitBookingId = res.data.data.booking_reference;
                        this.loadingRevisit = false;
                        this.$router.push(`/jobs/${this.jobId}/book-revisit`)
                    });
            },

            /** Revisit self serve */
            bookRevisitSelfServe() {
                this.loadingRevisit = true;
                this.$http.post(`/api/v4/orders/${this.job.order_id}/revisit-invitation`, {
                    "reason": "Sorry we missed you, click below to book a revisit."
                }).then(() => {
                    this.loadingRevisit = false;
                    this.$router.push('job');
                });
            },

            /** Check if in warranty repair */
            checkInWarrantyRequired() {
                if (this.job.in_warranty && this.selectedStatus === "1") {
                    this.serviceOrderRequired = true;
                } else {
                    this.updateDeviceStatus();
                }
            },

            /** Complete the service order */
            completeServiceOrder() {
                this.loading = true;
                this.soErrors = [];
                this.$http.post(`/api/v4/orders/devices/${this.deviceId}/finish-so`)
                    .catch(err => {
                        this.soErrors = err.response.data.messages;

                        if (this.soErrors[0] && this.soErrors[0] === "This service order is already closed.") {
                            this.updateDeviceStatus();
                        } else {
                            this.loading = false;
                        }
                    });
            },

            /** List images from firebase storage */
            listImages() {

                const listRef = firebase.storage().ref(`/images/${this.deviceId}`);

                let self = this;

                listRef.listAll().then(function (res) {

                    let urls = [];

                    res.items.forEach(function (itemRef) {

                        let splitPath = itemRef.fullPath.split("|");
                        let type = "other";

                        if (splitPath[1]) {
                            type = splitPath[1];
                        }

                        urls.push({
                            path: `https://firebasestorage.googleapis.com/v0/b/${itemRef.bucket}/o/${encodeURIComponent(itemRef.fullPath)}?alt=media&token=${uuidv4()}`,
                            type: type
                        });
                    });

                    self.imagePaths = urls;

                }).catch(function (error) {
                    console.log(error);
                });
            },

            /** Print the DPD label */
            printTheDpdLabel() {
                if (this.deviceAssets.length) {
                    this.printing = true;
                    this.printDpdLabel(this.deviceAssets[0].id).then(() => {
                        this.displayToast({text: 'The label is printing.', type: 'success'});
                        this.printing = false;
                    }).catch(e => {
                        this.displayToast({text: e, type: 'error'});
                        this.printing = false;
                    })
                }
            },

            /** Scroll to the progress steps when clicking the edit button */
            // scrollToProgressSteps() {
            //     this.$nextTick(() => {
            //         document.getElementById('progress-steps').scrollIntoView();
            //     })
            // },

            /** Update the device status and set review as complete */
            updateDeviceStatus() {
                this.revisitRequired = false;
                this.isSelfServe = false;

                this.deviceStatuses.forEach(status => {
                    if (status.id === this.selectedStatus) {
                        this.revisitRequired = status.revisit_required;

                        if (this.selectedStatus == 3 || this.selectedStatus == 4) {
                            this.isSelfServe = true;
                        }
                    }
                });

                this.setDeviceStatus({
                    jobId: this.jobId,
                    deviceId: this.deviceId,
                    status: this.selectedStatus
                }).then(() => {
                    // If status is Completed
                    if (this.selectedStatus === "1") {
                        this.loading = true;
                        this.setDeviceReviewComplete({
                            jobId: this.jobId,
                            deviceId: this.deviceId
                        }).then(() => {
                            this.setDeviceComplete({
                                jobId: this.jobId,
                                deviceId: this.deviceId
                            }).then(() => {
                                this.loadDeviceStatus({
                                    statusId: this.device.status_id
                                });
                                window.location.reload();
                            });
                        });
                        // If status is NOT Completed
                    } else if (this.selectedStatus === "37") {
                        this.loading = true;
                        this.setDeviceReviewComplete({
                            jobId: this.jobId,
                            deviceId: this.deviceId
                        }).then(() => {
                            this.setDeviceRepairedPendingQC({
                                jobId: this.jobId,
                                deviceId: this.deviceId
                            }).then(() => {
                                this.loadDeviceStatus({
                                    statusId: this.device.status_id
                                });
                                window.location.reload();
                            });
                        });
                        // If status is NOT Completed
                    } else if (this.selectedStatus !== "1" && this.selectedStatus !== "37") {
                        this.setDeviceNotComplete({
                            jobId: this.jobId,
                            deviceId: this.deviceId
                        }).then(() => {
                            this.loading = false;
                            this.loadDeviceStatus({
                                statusId: this.device.status_id
                            });

                            if (!this.revisitRequired) {
                                window.location.reload();
                            }
                        });
                    }
                });
            }
        }
    }
</script>

<style scoped>

</style>
